/**
 * 导航控制统一 hook — 收敛 mode 同步、移动端 input/gaming、Tab 快捷键。
 *
 * 替代原 useNavigationSync + useAvatarToggle，消除散落的三个 effect。
 */
import { type RefObject, useEffect } from 'react';
import { useViewerStore } from '@stores/viewerStore';
import type { RendererController } from '@renderer/index';

const isTouchDevice = () => 'ontouchstart' in window || navigator.maxTouchPoints > 0;

export function useNavigationController(controllerRef: RefObject<RendererController | null>) {
  const navMode = useViewerStore((s) => s.navigationMode);

  // ---- store → viewer 同步 + 移动端 input/gaming 控制 ----
  useEffect(() => {
    const ctrl = controllerRef.current;
    if (!ctrl) return;

    ctrl.navigation.setMode(navMode);

    // 移动端进入 fly/walk/avatar 时启用 touch 输入模式与 gamingControls；
    // 退出时恢复 desktop 模式，避免 orbit/anim 仍走 gaming 输入处理。
    const isGamingMode = navMode === 'fly' || navMode === 'walk' || navMode === 'avatar';
    if (isTouchDevice()) {
      ctrl.input.setMode(isGamingMode ? 'touch' : 'desktop');
      ctrl.input.setGamingControls(isGamingMode);
    }
  }, [controllerRef, navMode]);

  // ---- Tab 键切换 walk ↔ avatar ----
  useEffect(() => {
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key !== 'Tab') return;

      // 编辑态不拦截
      const target = e.target as HTMLElement | null;
      if (
        target &&
        (target.tagName === 'INPUT' || target.tagName === 'TEXTAREA' || target.isContentEditable)
      ) {
        return;
      }

      // 仅 walk/avatar 模式响应 Tab 切换第一/第三人称
      const current = useViewerStore.getState().navigationMode;
      if (current !== 'walk' && current !== 'avatar') return;

      e.preventDefault();
      e.stopPropagation();

      useViewerStore.getState().setNavigationMode(current === 'avatar' ? 'walk' : 'avatar');
    };

    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, []);
}
